import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/lib/supabase';
import { useSubscriptionWizard } from './SubscriptionWizardContext';
import SubscriptionWizardLayout from './SubscriptionWizardLayout';
import { toast } from 'sonner';

interface Customer {
  id: string;
  name: string;
  legal_name: string;
  business_identification_number: string;
  domain?: string;
  status: string;
  city?: string;
  state?: string;
}

const getInitials = (name: string): string => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

const formatCNPJ = (cnpj: string): string => {
  if (!cnpj) return '-';
  
  // Remove todos os caracteres não numéricos
  const numbers = cnpj.replace(/\D/g, '');
  
  // Verifica se tem 14 dígitos (CNPJ válido)
  if (numbers.length !== 14) {
    return cnpj; // Retorna o valor original se não for um CNPJ válido
  }
  
  // Aplica a formatação: XX.XXX.XXX/XXXX-XX
  return numbers.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    '$1.$2.$3/$4-$5'
  );
};

const handleImageError = (customerId: string) => {
  console.log(`Failed to load image for customer ${customerId}`);
};

const Step1Customer = () => {
  const navigate = useNavigate();
  const { state, setSelectedCustomer } = useSubscriptionWizard();
  const [searchQuery, setSearchQuery] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCustomers, setTotalCustomers] = useState(0);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchCustomers();
  }, [currentPage]); // Re-fetch when page changes

  useEffect(() => {
    // Debounce search query
    const handler = setTimeout(() => {
      if (searchQuery !== '') { // Avoid fetching on initial empty query
        setCurrentPage(1); // Reset to page 1 for new search
        fetchCustomers();
      } else {
        // Handle clearing the search
        fetchCustomers();
      }
    }, 500); // 500ms delay

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);


  const fetchCustomers = async () => {
    try {
      setLoading(true);
      
      const startIndex = (currentPage - 1) * itemsPerPage;
      
      let queryBuilder = supabase
        .from('customers')
        .select('id, name, legal_name, business_identification_number, domain, status, city, state', { count: 'exact' })
        .eq('status', 'active')
        .order('name')
        .range(startIndex, startIndex + itemsPerPage - 1);

      if (searchQuery) {
        const searchPattern = `%${searchQuery}%`;
        queryBuilder = queryBuilder.or(`name.ilike.${searchPattern},legal_name.ilike.${searchPattern},business_identification_number.ilike.${searchPattern}`);
      }

      const { data, error, count } = await queryBuilder;

      if (error) throw error;
      
      setCustomers(data || []);
      setTotalCustomers(count || 0);
      
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Falha ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1); // Reset page on new search
    fetchCustomers();
  };

  const handleSelectCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    navigate('../step1b', { relative: 'path' });
  };
  
  // Pagination calculations
  const totalPages = Math.ceil(totalCustomers / itemsPerPage);
  const currentCustomers = customers; // Data is already paginated from the backend

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Generate page numbers with ellipsis for better UX
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  return (
    <SubscriptionWizardLayout
      currentStep={0}
      totalSteps={5}
      title="Nova Assinatura - Selecionar Cliente"
      description="Selecione o cliente para o qual você deseja criar uma assinatura"
      onNext={() => {
        if (state.selectedCustomer) {
          navigate('../step2', { relative: 'path' });
        } else {
          toast.error('Por favor, selecione um cliente para continuar');
        }
      }}
      isNextDisabled={!state.selectedCustomer}
    >
      <div className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="search">Buscar Cliente</Label>
          <div className="flex gap-2">
            <Input
              id="search"
              placeholder="Nome, Razão Social ou CNPJ"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button type="button" onClick={handleSearch} variant="secondary">
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>CNPJ</TableHead>
                <TableHead>Cidade</TableHead>
                <TableHead>Estado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array(5).fill(0).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <div className="flex items-start gap-3">
                        <Skeleton className="h-10 w-10 rounded-full" />
                        <div className="space-y-1">
                          <Skeleton className="h-5 w-40" />
                          <Skeleton className="h-4 w-32" />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                  </TableRow>
                ))
              ) : currentCustomers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-24 text-center">
                    <div className="flex flex-col items-center justify-center text-muted-foreground">
                      <p className="mb-2">Nenhum cliente encontrado</p>
                      <Button variant="outline" size="sm" onClick={() => setSearchQuery('')}>Limpar busca</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                currentCustomers.map((customer) => (
                  <TableRow
                    key={customer.id}
                    className={`cursor-pointer transition-colors ${
                      state.selectedCustomer?.id === customer.id ? 'bg-primary/5' : 'hover:bg-muted/50'
                    }`}
                    onClick={() => handleSelectCustomer(customer)}
                  >
                    <TableCell>
                      <div className="flex items-start gap-3">
                        <Avatar className="h-10 w-10 border">
                          {customer.domain ? (
                            <AvatarImage 
                              src={`https://cdn.brandfetch.io/${customer.domain}?c=1idk4AUs2-buvooOUvQ`}
                              alt={customer.name}
                              onError={() => handleImageError(customer.id)}
                            />
                          ) : (
                            <AvatarFallback className="bg-primary/10 text-primary">
                              {getInitials(customer.name)}
                            </AvatarFallback>
                          )}
                        </Avatar>
                        <div className="space-y-1">
                          <div className="font-extrabold">{customer.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {customer.domain || '-'}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {formatCNPJ(customer.business_identification_number)}
                    </TableCell>
                    <TableCell>
                      {customer.city || '-'}
                    </TableCell>
                    <TableCell>
                      {customer.state || '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>

        {/* Pagination */}
        {totalCustomers > itemsPerPage && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Página {currentPage} de {totalPages} ({totalCustomers} clientes)
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Anterior
              </Button>
              
              <div className="flex items-center space-x-1">
                {getPageNumbers().map((page, index) => (
                  <div key={index}>
                    {page === '...' ? (
                      <span className="px-2 text-muted-foreground">...</span>
                    ) : (
                      <Button
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(page as number)}
                        className="w-8 h-8 p-0"
                      >
                        {page}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                Próximo
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {state.selectedCustomer && (
          <div className="bg-muted p-4 rounded-md">
            <div className="text-sm font-extrabold">Cliente Selecionado</div>
            <div className="font-extrabold mt-1">{state.selectedCustomer.name}</div>
            <div className="text-sm text-muted-foreground">
              {state.selectedCustomer.legal_name}
            </div>
            {state.selectedCustomer.business_identification_number && (
              <div className="text-sm text-muted-foreground mt-1">
                CNPJ: {formatCNPJ(state.selectedCustomer.business_identification_number)}
              </div>
            )}
          </div>
        )}
      </div>
    </SubscriptionWizardLayout>
  );
};

export default Step1Customer; 